import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { poolPagePath, type PoolStep } from "../lib/organise.js";
import { fetchTournaments, findEvent } from "../lib/tournaments.js";
import type { Tournament, TournamentEvent } from "../lib/types.js";
import { PoolSquads } from "./Franchises.js";
import { PoolLots } from "./TournamentLots.js";
import { PoolPanel } from "./TournamentSeeding.js";

/**
 * One pool's part of the auction set-up, a page per step.
 *
 * The tournament-wide Seeding, Lots and Teams pages used to stack every pool, so finding
 * women's doubles meant scrolling past men's whole average table. Each pool's share now has a
 * page of its own under the event, beside its manage screen. The tournament's URLs with an
 * event id were already taken by the panelist's ranking screen and the captain's lots page,
 * and an organiser can be a panelist too.
 *
 * Organisers only, like `/events/:id/manage`; the database refuses everybody else regardless.
 */

const STEP_TITLE: Record<PoolStep, string> = {
  squads: "Squads",
  seeding: "Seeding",
  lots: "Lots",
};

export function PoolSquadsPage() {
  return (
    <PoolPage step="squads">
      {(tournament, event) => <PoolSquads tournament={tournament} event={event} />}
    </PoolPage>
  );
}

export function PoolSeedingPage() {
  return (
    <PoolPage step="seeding">
      {(tournament, event) => <PoolPanel tournament={tournament} event={event} />}
    </PoolPage>
  );
}

export function PoolLotsManagePage() {
  return (
    <PoolPage step="lots">
      {(tournament, event) => <PoolLots tournament={tournament} event={event} />}
    </PoolPage>
  );
}

function PoolPage({
  step,
  children,
}: {
  step: PoolStep;
  children: (tournament: Tournament, event: TournamentEvent) => ReactNode;
}) {
  const { eventId } = useParams<{ eventId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });

  if (!hasRole(member, "organiser")) return <Navigate to={`/events/${eventId}`} replace />;

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  const found = eventId && tournaments ? findEvent(tournaments, eventId) : null;
  if (error || !found) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Notice tone="attention">
          {error ? "Couldn't load this event. Try again." : "That event isn't here any more."}
        </Notice>
      </main>
    );
  }

  const { tournament, event } = found;
  if (!tournament.auction) return <Navigate to={`/events/${event.id}/manage`} replace />;

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/events/${event.id}/manage`} className="micro mb-4 inline-block underline">
        ← {event.name}
      </Link>
      <PageHeader eyebrow={`Organise · ${event.name}`} title={STEP_TITLE[step]} />
      <PoolSwitcher tournament={tournament} current={event} step={step} />
      {children(tournament, event)}
    </main>
  );
}

/**
 * The same step for each of the tournament's pools, so doing seeding for every pool in a sitting
 * is a tap from one to the next rather than a trip back each time.
 */
function PoolSwitcher({
  tournament,
  current,
  step,
}: {
  tournament: Tournament;
  current: TournamentEvent;
  step: PoolStep;
}) {
  // The current pool is scrolled into sight: on a phone the row runs off the edge, and the pool
  // you are on is the one to see.
  const currentRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [current.id]);

  if (tournament.events.length < 2) return null;
  return (
    <nav aria-label="Pools" className="-mx-4 mb-6 overflow-x-auto px-4">
      <ul className="flex gap-2">
        {tournament.events.map((event) => {
          const on = event.id === current.id;
          return (
            <li key={event.id} className="shrink-0">
              <Link
                ref={on ? currentRef : undefined}
                to={poolPagePath(event.id, step)}
                aria-current={on ? "page" : undefined}
                className="flex min-h-[40px] items-center whitespace-nowrap border-[1.5px] px-3 text-[13px] font-semibold"
                style={{
                  borderColor: "var(--color-line)",
                  background: on ? "var(--color-line)" : "transparent",
                  color: on ? "var(--color-ground)" : "var(--color-ink)",
                }}
              >
                {event.name}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
